import { Card, Space, Tag, Typography } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";
import { CustomTooltip } from "src/components/CustomTooltip";

/**
 * AttentionItem Component
 *
 * A card component for displaying attention items that require focus,
 * such as risks, anomalies, or important updates.
 *
 * @example
 * ```tsx
 * <AttentionItem
 *   label="🔥 Reopened Ticket"
 *   description="Critical security vulnerability needs immediate attention"
 *   impactArea="security"
 *   evidence={["Issue #1234", "Security scan results"]}
 *   urgency="high"
 * />
 * ```
 */
export interface AttentionItemProps {
  /**
   * Label for the attention item (e.g., '🔥 Reopened Ticket', '🚨 Single Point of Failure')
   */
  label: string;

  /**
   * Description of the risk or anomaly
   */
  description: string;

  /**
   * Area of impact (e.g., 'quality', 'workflow', 'ownership')
   */
  impactArea: string;

  /**
   * Array of evidence points supporting the attention item
   */
  evidence: string[];

  /**
   * Urgency level of the attention item
   */
  urgency: "low" | "normal" | "high" | "critical";

  /**
   * Optional CSS class name for custom styling
   */
  className?: string;

  /**
   * Layout variant for the card: 'default' (vertical), 'alert' (highlighted), 'minimal' (text-only)
   */
  variant?: "default" | "alert" | "minimal";
}

const getUrgencyColor = (urgency: AttentionItemProps["urgency"]) => {
  switch (urgency) {
    case "critical": {
      return "error";
    }
    case "high": {
      return "warning";
    }
    case "low": {
      return "default";
    }
    default: {
      return "processing";
    }
  }
};

export const AttentionItem: React.FC<AttentionItemProps> = ({
  label,
  description,
  impactArea,
  evidence,
  urgency,
  className,
  variant = "default",
}) => {
  if (variant === "alert") {
    // Highlighted alert card
    return (
      <Card
        className={[
          className,
          "vzbl-attention-alert",
          "vzbl-panel-shadow",
          "flex-col-full",
        ]
          .filter(Boolean)
          .join(" ")}
        size="small"
        style={{ borderLeft: "6px solid #f5222d", background: "#fff1f0" }}
      >
        <Typography.Text className="vzbl-header-3">{label}</Typography.Text>
        <div className="vzbl-text-3">
          <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
            {description}
          </Markdown>
        </div>
        <div style={{ marginTop: 4 }}>
          <Tag color={getUrgencyColor(urgency)}>{urgency}</Tag>
        </div>
        {evidence?.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {evidence.map((item, idx) => (
              <li key={idx} style={{ color: "#a8071a" }}>
                <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
                  {item}
                </Markdown>
              </li>
            ))}
          </ul>
        )}
      </Card>
    );
  }
  if (variant === "minimal") {
    // Minimal, text-only card
    return (
      <div
        className={[className, "vzbl-attention-minimal", "vzbl-header-3"]
          .filter(Boolean)
          .join(" ")}
      >
        <strong>{label}</strong>:
        <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
          {description}
        </Markdown>
      </div>
    );
  }
  // Default vertical card
  return (
    <Card
      className={[
        className,
        "vzbl-attention-default",
        "vzbl-panel-shadow",
        "flex-col-full",
      ]
        .filter(Boolean)
        .join(" ")}
      size="small"
      bodyStyle={{ height: "100%" }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Typography.Text className="vzbl-header-4 single-line">
                <CustomTooltip content={stripMarkdown(label)}>
                  {stripMarkdown(label)}
                </CustomTooltip>
              </Typography.Text>

              <Typography.Text className="vzbl-text-3">
                <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
                  {description}
                </Markdown>
              </Typography.Text>
            </Space>

            {evidence?.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {evidence.map((item, idx) => (
                  <li key={idx}>
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      skipHtml={true}
                      className="vzbl-text-3"
                    >
                      {item}
                    </Markdown>
                  </li>
                ))}
              </ul>
            )}
          </Space>
        </div>
        {/* Tag row at the bottom */}
        <div
          style={{
            marginTop: "auto",
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Tag className="vzbl-pill-sm">{impactArea}</Tag>
          <Tag className="vzbl-pill-sm" color={getUrgencyColor(urgency)}>
            {urgency}
          </Tag>
        </div>
      </div>
    </Card>
  );
};
